import { useState, useEffect, useRef, useMemo } from 'react';
import { useContacts } from '../contexts/ContactsContext';
import { useInvoices } from '../contexts/InvoicesContext';
import { setDeepLink } from '../utils/deepLink';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';

function highlight(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: YELLOW, color: NAVY, borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// pages: the sidebar items this person sees here (nav/views.sidebarViews), already
// labelled. isPersonal: personalFlag() of the active workspace. Invoices are loaded by
// business, not by workspace, so a Personal search would otherwise list them and open
// the Business invoicing page inside Personal; they are offered only when the
// workspace is known to be a Business one.
export function searchAll(query, notes = [], contacts = [], invoices = [], tasks = [], pages = [], isPersonal = null) {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  const results = [];

  // Pages, first and at most five, so a name like "Contacts" finds the page as well
  // as anyone whose company is called that.
  for (const p of pages.filter(p => p.label?.toLowerCase().includes(q)).slice(0, 5)) {
    results.push({
      id: `page_${p.key}`,
      type: 'page',
      icon: p.icon,
      label: p.label,
      sub: 'Page',
      data: p,
      section: 'Pages',
    });
  }

  // Tasks — from the real workspace-scoped task list (passed in), not localStorage.
  for (const t of tasks) {
    if (
      t.title?.toLowerCase().includes(q) ||
      t.details?.toLowerCase().includes(q) ||
      t.notes?.toLowerCase().includes(q)
    ) {
      results.push({
        id: `task_${t.id}`,
        type: 'task',
        icon: t.taskType === 'Meeting' ? '📅' : '✅',
        label: t.title,
        sub: t.date ? new Date(t.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'No date',
        data: t,
        section: 'Tasks',
      });
    }
  }

  // Notes (from in-memory prop)
  for (const n of notes) {
    const noteText = n.text || n.body || '';
    if (n.title?.toLowerCase().includes(q) || noteText.toLowerCase().includes(q)) {
      results.push({
        id: `note_${n.id}`,
        type: 'note',
        icon: '📝',
        label: n.title || 'Untitled note',
        sub: noteText.slice(0, 60),
        data: n,
        section: 'Notes',
      });
    }
  }

  // Contacts (from in-memory context)
  for (const c of contacts) {
    if (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q)
    ) {
      results.push({
        id: `contact_${c.id}`,
        type: 'contact',
        icon: '👤',
        label: c.name,
        sub: [c.company, c.email].filter(Boolean).join(' · '),
        data: c,
        section: 'Contacts',
      });
    }
  }

  // Invoices: Business workspaces only (see above).
  for (const inv of (isPersonal === false ? invoices : [])) {
    if (
      inv.contactName?.toLowerCase().includes(q) ||
      inv.number?.toLowerCase().includes(q) ||
      inv.notes?.toLowerCase().includes(q)
    ) {
      results.push({
        id: `inv_${inv.id}`,
        type: 'invoice',
        icon: '🧾',
        label: inv.number || `Invoice ${inv.id}`,
        sub: `${inv.contactName || ''} · ${inv.status || ''}`,
        data: inv,
        section: 'Invoices',
      });
    }
  }

  return results.slice(0, 12);
}

// What selecting a result does. Exported for SearchOverlay.test.js.
export function openResult(result, { onNavigate, onViewTask, onViewNote }) {
  switch (result.type) {
    case 'page':
      if (onNavigate) onNavigate(result.data.key);
      return;
    case 'task':
      if (onViewTask) { onViewTask(result.data); return; }
      setDeepLink('task', result.data.id);
      if (onNavigate) onNavigate('Tasks');
      return;
    case 'note':
      // Decision 90: a note opens in the Notes editor view (the deep link 'note'), never a popup.
      if (onViewNote) { onViewNote(result.data); return; }
      setDeepLink('note', result.data.id);
      if (onNavigate) onNavigate('Notes');
      return;
    case 'contact':
      setDeepLink('contact', result.data.id);
      if (onNavigate) onNavigate('Contacts');
      return;
    case 'invoice':
      setDeepLink('invoice', result.data.id);
      if (onNavigate) onNavigate('Invoices');
      return;
    default:
      return;
  }
}

const SECTION_ICON = {
  Pages: '🧭',
  Tasks: '✅',
  Notes: '📝',
  Contacts: '👤',
  Invoices: '🧾',
};

export default function SearchOverlay({ onClose, onNavigate, onViewTask, onViewNote, notes = [], tasks = [], pages = [], isPersonal = null }) {
  const { contacts } = useContacts();
  const { invoices } = useInvoices();
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(
    () => searchAll(query, notes, contacts, invoices, tasks, pages, isPersonal),
    [query, notes, contacts, invoices, tasks, pages, isPersonal]
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  // Every result opens a real page, and a record opens ITSELF rather than the list it
  // lives in: the item deep link is published before navigating, so the section opens
  // it on mount (or on the aidey:open-item event if it is already showing).
  function handleSelect(result) {
    onClose();
    openResult(result, { onNavigate, onViewTask, onViewNote });
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[selectedIdx]) { handleSelect(results[selectedIdx]); }
  }

  // Group results by section
  const grouped = useMemo(() => {
    const map = {};
    results.forEach(r => {
      if (!map[r.section]) map[r.section] = [];
      map[r.section].push(r);
    });
    return Object.entries(map);
  }, [results]);

  let flatIndex = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 'var(--z-modal)', backdropFilter: 'blur(2px)',
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed', top: '10vh', left: '50%', transform: 'translateX(-50%)',
          width: '100%', maxWidth: 580,
          background: '#fff', borderRadius: 14,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          zIndex: 'var(--z-modal)', overflow: 'hidden',
          fontFamily: "'Raleway', sans-serif",
        }}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0.9rem 1rem', borderBottom: '1px solid #f0f0f0', gap: '0.6rem' }}>
          <span style={{ fontSize: '1.15rem', color: '#aaa' }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isPersonal === false ? 'Search pages, tasks, notes, contacts, invoices…' : 'Search pages, tasks, notes, contacts…'}
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: '1rem', fontFamily: "'Raleway', sans-serif",
              color: '#333',
            }}
          />
          <kbd style={{ fontSize: '0.72rem', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, padding: '2px 6px', color: '#888', fontFamily: 'monospace' }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {query.length < 2 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#bbb', fontSize: '0.88rem' }}>
              {isPersonal === false
                ? 'Type to search pages, tasks, notes, contacts and invoices'
                : 'Type to search pages, tasks, notes and contacts'}
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#bbb', fontSize: '0.88rem' }}>
              No results for "{query}"
            </div>
          ) : (
            grouped.map(([section, items]) => (
              <div key={section}>
                <div style={{ padding: '0.5rem 1rem 0.2rem', fontSize: '0.68rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {SECTION_ICON[section]} {section}
                </div>
                {items.map(result => {
                  const isSelected = flatIndex === selectedIdx;
                  const currentIndex = flatIndex++;
                  return (
                    <div
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setSelectedIdx(currentIndex)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.6rem 1rem',
                        background: isSelected ? '#f0f5f9' : '#fff',
                        cursor: 'pointer',
                        borderLeft: isSelected ? `3px solid ${NAVY}` : '3px solid transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{result.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {highlight(result.label, query)}
                        </div>
                        {result.sub && (
                          <div style={{ fontSize: '0.75rem', color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {highlight(result.sub, query)}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <kbd style={{ fontSize: '0.68rem', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, padding: '2px 5px', color: '#888', fontFamily: 'monospace', flexShrink: 0 }}>
                          ↵
                        </kbd>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #f5f5f5', display: 'flex', gap: '1rem', fontSize: '0.72rem', color: '#ccc' }}>
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>ESC close</span>
        </div>
      </div>
    </>
  );
}
